"use client"

import {useTranslations} from "next-intl"
import {localizeConversationDisplay} from "@/lib/i18n/conversation-copy"
import { ConversationBubble } from "@/components/ui/conversation/conversation-bubble"
import { ConversationMessageText } from "@/components/ui/conversation/conversation-message-text"
import { ConversationMessageTrailer } from "@/components/ui/conversation/conversation-message-trailer"
import { ConversationMedia } from "@/components/ui/conversation/conversation-media"
import type { UnifiedThreadMessage as ThreadMessage } from "@/lib/inbox/types"

/**
 * One message, read-only.
 *
 * The customer sits on the left and everything sent from this side on the right,
 * with the agent and a colleague distinguished by colour and by name — a manager
 * reply that looks like the agent's is how an operator ends up answering a
 * question a teammate already answered.
 */
export function InboxThreadMessageBubble({
  message,
  timezone,
}: {
  message: ThreadMessage
  timezone: string
}) {
  const tCopy = useTranslations("ConversationCopy")
  const fromCustomer = message.role === "customer"

  return (
    <ConversationBubble
      align={fromCustomer ? "start" : "end"}
      tone={message.role}
      authorLabel={localizeConversationDisplay(message, tCopy).label}
      timestamp={message.timestamp}
      timezone={timezone}
      className="max-w-[62%]"
      trailer={message.trailer && <ConversationMessageTrailer trailer={message.trailer} />}
    >
      {message.media && <ConversationMedia media={message.media} />}
      {message.html ? (
        // Sanitized server-side by `sanitizeEmailHtml`, the same treatment the
        // email viewer already gives a mail body. Its own scroll container:
        // a wide table in a newsletter must not push the pane sideways. A `<pre>`
        // wraps instead of scrolling — mail uses it for values, not for code, and
        // a contact form's message is meant to be read, not scrolled through.
        <div
          className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto text-sm [&_pre]:whitespace-pre-wrap [&_pre]:break-words"
          dangerouslySetInnerHTML={{ __html: message.html }}
        />
      ) : (
        message.text && <ConversationMessageText text={message.text} markdown={message.markdown} />
      )}
    </ConversationBubble>
  )
}
